(() => {
  if (window.EkselansOrderLimits) return;
  const config = window.EkselansOrderLimitsConfig;
  if (!config) return;
  const originalFetch = window.fetch.bind(window);
  const products = new Map();
  const variants = new Map();
  let cart = config.cart || { items: [] };
  let queue = Promise.resolve();
  let noticeTimer;
  let scanTimer;

  function maximum(tags) {
    const limits = (tags || []).map(tag => /^max-(1|3|5)$/.exec(String(tag))).filter(Boolean).map(match => Number(match[1]));
    return limits.length ? Math.min(...limits) : 0;
  }
  function register(product) {
    if (!product || !product.id) return;
    const normalized = { ...product, maximum: maximum(product.tags) };
    products.set(String(product.id), normalized);
    (product.variants || []).forEach(variant => variants.set(String(variant.id || variant), normalized));
  }
  function scanMetadata() {
    document.querySelectorAll('script[data-ekselans-order-product]:not([data-order-read])').forEach(node => {
      try { register(JSON.parse(node.textContent)); node.dataset.orderRead = 'true'; } catch (error) { console.error('Order limit metadata', error); }
    });
  }
  function message(limit) {
    return config.message.replace('[max]', String(limit));
  }
  function notify(text) {
    let notice = document.getElementById('ekselans-order-limit-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'ekselans-order-limit-notice';
      notice.setAttribute('role', 'alert');
      document.body.appendChild(notice);
    }
    notice.textContent = text;
    notice.hidden = false;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { notice.hidden = true; }, 6500);
  }
  function failure(text) {
    const error = new Error(text);
    error.orderLimit = true;
    return error;
  }
  async function refreshCart() {
    const response = await originalFetch(config.root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw failure(config.retry);
    cart = await response.json();
    scanMetadata();
    await Promise.all(cart.items.filter(item => !products.has(String(item.product_id))).map(async item => {
      const response = await originalFetch(config.root + 'products/' + encodeURIComponent(item.handle) + '.js');
      if (!response.ok) throw failure(config.retry);
      register(await response.json());
    }));
    return cart;
  }
  function decode(body) {
    if (!body) return {};
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch (_) { return decode(new URLSearchParams(body)); }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const data = {};
      for (const [key, value] of body.entries()) {
        const itemMatch = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(key);
        const updateMatch = /^updates\[(.*)\]$/.exec(key);
        if (itemMatch) {
          data.items ||= [];
          data.items[Number(itemMatch[1])] ||= {};
          data.items[Number(itemMatch[1])][itemMatch[2]] = value;
        } else if (updateMatch) {
          if (updateMatch[1] === '') {
            data.updates ||= [];
            data.updates.push(value);
          } else {
            data.updates ||= {};
            data.updates[updateMatch[1]] = value;
          }
        } else data[key] = value;
      }
      return data;
    }
    return body;
  }
  function actionFor(url) {
    try {
      const parsed = new URL(url, location.href);
      if (parsed.origin !== location.origin) return null;
      return /\/cart\/(add|change|update|clear)(?:\.js)?\/?$/.exec(parsed.pathname)?.[1] || null;
    } catch (_) { return null; }
  }
  function project(action, data, currentCart) {
    const lines = currentCart.items.map(item => ({ key: item.key, variant: String(item.variant_id || item.id), product: String(item.product_id), quantity: Number(item.quantity) }));
    const before = new Map();
    lines.forEach(item => before.set(item.product, (before.get(item.product) || 0) + item.quantity));
    if (action === 'clear') return null;
    if (action === 'add') {
      (data.items || [data]).filter(Boolean).forEach(item => {
        const product = variants.get(String(item.id));
        if (!product) throw failure(config.retry);
        lines.push({ product: String(product.id), quantity: Number(item.quantity ?? 1) });
      });
    } else if (action === 'change') {
      const index = data.line ? Number(data.line) - 1 : lines.findIndex(item => item.key === String(data.id) || item.variant === String(data.id));
      if (lines[index] && data.quantity != null) lines[index].quantity = Number(data.quantity);
    } else if (action === 'update' && data.updates) {
      if (Array.isArray(data.updates)) {
        data.updates.forEach((quantity, index) => { if (lines[index]) lines[index].quantity = Number(quantity); });
      } else {
        for (const [id, quantity] of Object.entries(data.updates)) {
          const line = lines.find(item => item.key === id || item.variant === id);
          if (line) line.quantity = Number(quantity);
          else {
            const product = variants.get(id);
            if (!product && Number(quantity) > 0) throw failure(config.retry);
            if (product) lines.push({ product: String(product.id), quantity: Number(quantity) });
          }
        }
      }
    }
    const totals = new Map();
    lines.forEach(item => totals.set(item.product, (totals.get(item.product) || 0) + item.quantity));
    for (const [id, total] of totals) {
      const limit = products.get(id)?.maximum || 0;
      if (limit && total > limit && total > (before.get(id) || 0)) return limit;
    }
    return null;
  }
  async function check(action, data) {
    await refreshCart();
    const limit = project(action, data, cart);
    if (limit) throw failure(message(limit));
  }
  function serialize(work) {
    const task = queue.then(work, work);
    queue = task.catch(() => {});
    return task;
  }
  function report(error) {
    const text = error.orderLimit ? error.message : config.retry;
    notify(text);
    return text;
  }
  function scheduleScan() {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(updateInputs, 60);
  }
  function cartLine(input) {
    const lineKey = input.dataset.line || input.closest('[data-line]')?.dataset.line;
    if (lineKey) return cart.items.find(item => item.key === lineKey);
    const index = Number(input.dataset.index);
    return Number.isInteger(index) && index > 0 ? cart.items[index - 1] : null;
  }
  function boundFor(input) {
    if (input.matches('[data-cart-quantity]') || input.closest('cart-update-quantity')) {
      const line = cartLine(input);
      if (!line) return null;
      const product = products.get(String(line.product_id));
      if (!product?.maximum) return null;
      const others = cart.items.filter(item => String(item.product_id) === String(line.product_id) && item.key !== line.key).reduce((sum, item) => sum + item.quantity, 0);
      return { max: Math.max(0, product.maximum - others), limit: product.maximum };
    }
    const id = input.dataset.product || input.closest('form')?.dataset.productId || config.productId;
    const product = products.get(String(id));
    return product?.maximum ? { max: product.maximum, limit: product.maximum } : null;
  }
  function updateInputs() {
    scanMetadata();
    document.querySelectorAll('input[name="quantity"]:not([type="hidden"]), input[name^="updates["]').forEach(input => {
      const bound = boundFor(input);
      if (!bound) return;
      const existing = Number(input.getAttribute('max'));
      const targetMax = input.dataset.orderMaximum ? Number(input.dataset.orderBaseMaximum) || bound.max : existing > 0 ? existing : bound.max;
      if (!input.dataset.orderMaximum) input.dataset.orderBaseMaximum = existing > 0 ? String(existing) : '';
      input.dataset.orderMaximum = String(bound.limit);
      const newMax = String(Math.min(targetMax, bound.max));
      if (input.getAttribute('max') !== newMax) input.setAttribute('max', newMax);
      const wrapper = input.closest('quantity-input, cart-update-quantity, .quantity__container');
      const plus = wrapper?.querySelector('[name="plus"], [data-plus-quantity-cart]');
      if (plus) plus.setAttribute('aria-disabled', String(Number(input.value) >= Number(newMax)));
      if (wrapper && !input.matches('[data-cart-quantity]') && !input.closest('cart-update-quantity')) {
        let hint = wrapper.querySelector('[data-order-limit-hint]');
        if (!hint) {
          hint = document.createElement('p');
          hint.dataset.orderLimitHint = 'true';
          hint.style.cssText = 'font-size:13px;line-height:1.5;margin:8px 0 0;clear:both;';
          wrapper.appendChild(hint);
        }
        const text = message(bound.limit);
        if (hint.textContent !== text) hint.textContent = text;
      }
    });
  }
  window.fetch = function(resource, options) {
    const action = actionFor(typeof resource === 'string' || resource instanceof URL ? resource : resource.url);
    const method = (options?.method || resource?.method || 'GET').toUpperCase();
    if (!action || method !== 'POST') return originalFetch(resource, options);
    return serialize(async () => {
      let body = options?.body;
      if (body === undefined && resource instanceof Request) {
        const type = resource.headers.get('content-type') || '';
        body = type.includes('multipart/form-data') ? await resource.clone().formData() : await resource.clone().text();
      }
      try { await check(action, decode(body)); }
      catch (error) {
        const text = report(error);
        return new Response(JSON.stringify({ status: 422, message: text, description: text }), { status: 422, headers: { 'Content-Type': 'application/json' } });
      }
      const response = await originalFetch(resource, options);
      await refreshCart().catch(() => {});
      scheduleScan();
      return response;
    });
  };
  function installJQueryGuard() {
    const $ = window.jQuery;
    if (!$ || $.ajax.__ekselansOrderGuard) return;
    const originalAjax = $.ajax;
    function guardedAjax(url, settings) {
      const opts = typeof url === 'string' ? { ...(settings || {}), url } : { ...url };
      const action = actionFor(opts.url || location.href);
      if (!action || String(opts.type || opts.method || 'GET').toUpperCase() !== 'POST') return originalAjax.apply(this, arguments);
      const context = this;
      const deferred = $.Deferred();
      let active;
      let cancelled = false;
      const proxy = deferred.promise({ abort() { cancelled = true; active?.abort(); return this; } });
      serialize(async () => {
        if (cancelled) return;
        try { await check(action, decode(opts.data)); }
        catch (error) {
          const text = report(error);
          const response = { status: 422, message: text, description: text };
          Object.assign(proxy, { status: 422, readyState: 4, responseJSON: response, responseText: JSON.stringify(response) });
          document.querySelectorAll('.is-loading, .loading').forEach(node => { if (node.matches('button, a, [data-btn-addToCart]')) { node.classList.remove('is-loading', 'loading'); node.removeAttribute('disabled'); } });
          try { opts.error?.call(opts.context || opts, proxy, 'error', text); } catch (_) {}
          try { opts.complete?.call(opts.context || opts, proxy, 'error'); } catch (_) {}
          deferred.reject(proxy, 'error', text);
          scheduleScan();
          return;
        }
        if (cancelled) return;
        await new Promise(resolve => {
          active = originalAjax.call(context, opts);
          active.done(function() { deferred.resolveWith(this, arguments); });
          active.fail(function() { deferred.rejectWith(this, arguments); });
          active.always(resolve);
        });
        await refreshCart().catch(() => {});
        scheduleScan();
      }).catch(error => { report(error); deferred.reject(proxy, 'error', error.message); });
      return proxy;
    }
    guardedAjax.__ekselansOrderGuard = true;
    $.ajax = guardedAjax;
  }
  const replaying = new WeakSet();
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || replaying.has(form)) return;
    const url = new URL(form.action, location.href);
    let action = actionFor(url.href);
    const checkout = event.submitter?.name === 'checkout' || event.submitter?.id?.includes('checkout');
    if (!action && /\/cart\/?$/.test(url.pathname) && form.method.toUpperCase() === 'POST') action = 'update';
    if (!action && !checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    serialize(async () => {
      try {
        if (checkout) {
          await refreshCart();
          const over = cart.items.map(item => String(item.product_id)).find(id => {
            const max = products.get(id)?.maximum;
            return max && cart.items.filter(item => String(item.product_id) === id).reduce((sum, item) => sum + item.quantity, 0) > max;
          });
          if (over) throw failure(message(products.get(over).maximum));
        } else await check(action, decode(new FormData(form)));
        replaying.add(form);
        try { form.requestSubmit(event.submitter || undefined); } finally { replaying.delete(form); }
      } catch (error) { report(error); }
    });
  }, true);
  document.addEventListener('click', event => {
    const plus = event.target.closest('[name="plus"], [data-plus-quantity-cart]');
    if (plus) {
      const wrapper = plus.closest('quantity-input, cart-update-quantity, .quantity__container');
      const input = wrapper?.querySelector('input[type="number"]');
      const bound = input && boundFor(input);
      if (bound && Number(input.value) >= Math.min(bound.max, Number(input.max) || bound.max)) {
        event.preventDefault(); event.stopImmediatePropagation(); notify(message(bound.limit)); return;
      }
    }
    const checkout = event.target.closest('#cart-checkout, #cart-sidebar-checkout, a[href*="/checkout"]');
    if (!checkout || replaying.has(checkout)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    serialize(async () => {
      try {
        await refreshCart();
        for (const product of products.values()) {
          const total = cart.items.filter(item => String(item.product_id) === String(product.id)).reduce((sum, item) => sum + item.quantity, 0);
          if (product.maximum && total > product.maximum) throw failure(message(product.maximum));
        }
        replaying.add(checkout);
        try { checkout.click(); } finally { replaying.delete(checkout); }
      } catch (error) { report(error); }
    });
  }, true);
  ['input', 'change'].forEach(type => document.addEventListener(type, event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;
    const bound = boundFor(input);
    if (bound && Number(input.value) > bound.max) {
      input.value = String(bound.max);
      notify(message(bound.limit));
    }
    scheduleScan();
  }, true));
  (config.products || []).forEach(register);
  installJQueryGuard();
  document.addEventListener('DOMContentLoaded', () => {
    installJQueryGuard();
    updateInputs();
    new MutationObserver(mutations => {
      if (mutations.some(mutation => mutation.addedNodes.length || mutation.removedNodes.length)) scheduleScan();
    }).observe(document.body, { childList: true, subtree: true });
    refreshCart().then(scheduleScan).catch(() => {});
  }, { once: true });
  window.EkselansOrderLimits = { maximum, project, decode, register, refresh: refreshCart, version: '1.0.0' };
})();